export const ATS_INTERACTIVE_STAGES = ["additional_info", "video", "headshot"] as const;

export const ATS_MESSAGE_MAX_LENGTH = 4000;

// Pay arrangements an offer can state. PAY_UNIT in atsOfferService must name every one, or a type
// with no unit renders "$18.50" with "per hour" silently missing on a document the candidate signs.

export const ATS_PAY_TYPES = [
  { value: "hourly", label: "Hourly" },
  { value: "salary", label: "Salary" },
  { value: "per_visit", label: "Per visit" },
  { value: "per_diem", label: "Per diem" },
] as const;

// Merge placeholders for SMS template authors, 1:1 with `buildOfferMergeData` in atsOfferService,
// shared so the editor needs no server code.
export const ATS_SMS_MERGE_FIELDS = [
  { key: "candidateName", label: "Candidate Name" },
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "jobTitle", label: "Job Title" },
  { key: "department", label: "Department" },
  { key: "location", label: "Location" },
  { key: "today", label: "Today's Date" },
] as const;

/* Merge fields a front-desk reply may use, deliberately not the hiring set. No caller-name field:
 * the resident on a call must never be merged into a text to an unverified person. */
export const FRONT_DESK_SMS_MERGE_FIELDS = [
  { key: "facilityName", label: "Community Name" },
  { key: "today", label: "Today's Date" },
] as const;

// Approving at Review sends the candidate straight to the offer letter. The old stage's name stays
// in labels and ATS_ALL_STAGES so history reads.
export const ATS_PIPELINE_STAGES = [
  "pre_screening",
  "screening",
  "job_application",
  "review",
  "offer_letter",
  "hired",
] as const;

type AtsPipelineStage = (typeof ATS_PIPELINE_STAGES)[number];

// The stage sequence a specific job actually runs. With no interactive stages
// enabled this is exactly ATS_PIPELINE_STAGES, so existing jobs are unaffected.
export function atsEffectivePipeline(
  interactiveStages: readonly string[] | null | undefined,
  // True when the job has a form template backed by a signing template. "signatures" is not opt-in
  // like video: a job has documents to sign or not. Without this the auto-issue branch in POST
  // /advance could never fire.
  hasSignableForms: boolean = false,
  skipOfferLetter: boolean = false,
): AtsStage[] {
  const enabled = ATS_INTERACTIVE_STAGES.filter((s) => (interactiveStages ?? []).includes(s));
  const out: AtsStage[] = [];
  for (const core of ATS_PIPELINE_STAGES) {
    // The one core stage a job may drop. Signing still happens if the job has
    // documents — paperwork does not depend on there having been a letter.
    if (core === "offer_letter" && skipOfferLetter) {
      if (hasSignableForms) out.push("signatures");
      continue;
    }
    out.push(core);
    for (const interactive of enabled) {
      if (ATS_INTERACTIVE_STAGE_ANCHOR[interactive] === core) out.push(interactive);
    }
    // Signing sits after the offer is out and before the hire is final, so the
    // paperwork is done while the candidate is still in the pipeline.
    if (core === "offer_letter" && hasSignableForms) out.push("signatures");
  }
  return out;
}

export const ATS_STAGE_STATUSES = [
  "pending",
  "in_progress",
  "completed",
  "rejected",
  "reset",
  "nonresponsive",
  "hold",
] as const;

export const ATS_STAGE_LABELS: Record<string, string> = {
  pre_screening: "Pre-screening",
  screening: "Screening",
  job_application: "Job application",
  review: "Review",
  approved: "Approved",
  offer_letter: "Offer letter",
  hired: "Hired",
  video: "Video",
  additional_info: "Additional info",
  documents: "Documents",
  signatures: "Signatures",
  headshot: "Headshot",
  converted: "Converted",
  rejected: "Rejected",
};

/* Sentences continuing the person's name, read by recruiters and admins. Every member of
 * `event_type` must be present, or the raw key prints. */

export const ATS_EVENT_LABELS: Record<string, string> = {
  created: "applied",
  advanced: "moved forward",
  reverted: "was moved back a stage",
  rejected: "was not taken forward",
  status_changed: "had their status changed",
  converted: "became an employee",
  bypassed: "was moved on without meeting the requirements",
  screening_passed: "passed screening",
  screening_failed: "did not pass screening",
  documents_requested: "was asked for documents",
  document_uploaded: "uploaded a document",
  document_uploaded_by_staff: "had a document uploaded for them",
  document_waived: "had a document requirement waived",
  offer_sent: "was sent an offer",
  offer_accepted: "accepted the offer",
  offer_declined: "declined the offer",
  offer_revoked: "had their offer withdrawn",
  offer_acceptance_requested: "was asked to complete their offer paperwork",
  signing_requested: "was sent documents to sign",
  signing_completed: "signed their documents",
  document_voided: "had a document cancelled",
  document_removed: "had a document removed from their record",
  note_added: "had a note added",
  candidate_notified: "was sent an update",
};

export const ATS_STATUS_LABELS: Record<string, string> = {
  pending: "Pending",
  in_progress: "In progress",
  completed: "Completed",
  rejected: "Rejected",
  reset: "Reset",
  nonresponsive: "Non-responsive",
  hold: "On hold",
};

export type AtsStage = (typeof ATS_ALL_STAGES)[number];

// The core stage each follows: info and video come once the application is in (directly after
// screening), headshot once the offer is out.
const ATS_INTERACTIVE_STAGE_ANCHOR: Record<AtsInteractiveStage, AtsPipelineStage> = {
  additional_info: "job_application",
  video: "job_application",
  headshot: "offer_letter",
};

// All stages (including terminal off-pipeline and legacy stages kept for backward compat).
const ATS_ALL_STAGES = [
  ...ATS_PIPELINE_STAGES,
  "approved",
  "video",
  "additional_info",
  "documents",
  "signatures",
  "headshot",
  "converted",
  "rejected",
] as const;

export type AtsInteractiveStage = (typeof ATS_INTERACTIVE_STAGES)[number];
